import {
	MDBBtn,
	MDBTabContent,
	MDBTabPane,
	MDBModal,
	MDBModalBody,
	MDBModalFooter,
	MDBIcon,
} from "mdbreact";
import React, { useEffect, useRef, useState } from "react";
import "../../ui/theme.css";
import styles from "./ChildRegister.module.scss";
import { data, details } from "./childProfileData";
import { useSelector, useDispatch } from "react-redux";
import useForms from "./useForms";
import InputRenderer from "./InputRenderer";
import InputImage from "./InputImage";
import useFetch from "../../CustomHooks/useFetch";
import { setParents } from "../../../Redux/actions/actionCreator/data.action";
import ChildTransportation from "../ChildDashboard/ChildTransportation";
import Loader from "../ChildDashboard/DevelopmentDasboard/charts/Loader";
import { countries } from "./countriesListWithStates";
import Button from "../../../GeneralComponents/Button";
import SnackBar from "../../../GeneralComponents/SnackBar";
import DocumentsTab from "./DocumentsTab";
import { useTranslation } from "react-i18next";
import TutorialLink from "../../ui/TutorialLink";
import { registerChildLabel } from "../childKeywordLabels";

import {
	nestedGroup,
	addSectionObject
} from "../../../Redux/actions/actionCreator/data.action";

const fastTrackExcludedRequiredFields = new Set(["howDidYouKnowAboutUsOthers"]);

const isFastTrackRequiredField = (field) =>
	field?.required && !fastTrackExcludedRequiredFields.has(field.name);

const getFastTrackFields = (fields = []) =>
	fields.reduce((requiredFields, field) => {
		if (Array.isArray(field)) {
			const hasRequiredField = field.some(isFastTrackRequiredField);
			return hasRequiredField ? [...requiredFields, field] : requiredFields;
		}

		return isFastTrackRequiredField(field)
			? [...requiredFields, field]
			: requiredFields;
	}, []);

const getFastTrackRegistrationData = (registrationData) =>
	registrationData
		.map((step, originalIndex) => {
			const fastTrackSections = step.section
				?.map((section) => ({
					...section,
					data: getFastTrackFields(section.data),
				}))
				.filter((section) => section.data.length > 0);
			const fastTrackFields = getFastTrackFields(step.data);

			return {
				...step,
				originalIndex,
				data: fastTrackFields,
				section: fastTrackSections,
			};
		})
		.filter((step) => step.data.length > 0 || step.section?.length > 0);

const getInitials = (firstName, lastName) => {
	const first = `${firstName || ""}`.trim().charAt(0);
	const last = `${lastName || ""}`.trim().charAt(0);
	return `${first}${last}`.toUpperCase() || "?";
};

const withoutChildPhotoField = (fields = []) =>
	fields.filter(
		(field) => !(field && !Array.isArray(field) && field.name === "photo")
	);

const emptySelectValues = new Set([
	"",
	"Select An Option",
	"Select an option",
	"Select an Option",
	"Select A State",
	"Select A Country",
	"Dropdown of States",
	"Dropdown of Countries",
]);

const allowedChildBillingTypes = ["Monthly", "Term"];

const getStoredConfig = () => {
	try {
		return JSON.parse(localStorage.getItem("config") || "{}");
	} catch (error) {
		return {};
	}
};

const normalizeChildBillingType = (value) => {
	const normalizedValue = `${value || ""}`.trim().toLowerCase();
	if (["monthly", "month", "monthy"].includes(normalizedValue)) return "Monthly";
	return "Term";
};

const getChildBillingTypeFromSources = (...sources) => {
	for (const source of sources) {
		const value =
			source?.childBillingType ||
			source?.daycare?.childBillingType ||
			source?.labels?.childBillingType ||
			source?.data?.childBillingType;
		if (value) {
			return normalizeChildBillingType(value);
		}
	}

	return "Term";
};

const applyConfiguredBillingType = (steps, billingType) => {
	const normalizedBillingType = normalizeChildBillingType(billingType);
	const feePeriod = normalizedBillingType === "Monthly" ? "Month" : "Term";

	const updateFields = (fields = []) => {
		fields.forEach((field) => {
			if (Array.isArray(field)) {
				updateFields(field);
				return;
			}

			if (field?.name === "billing") {
				field.initialValue = normalizedBillingType;
				field.options = [normalizedBillingType];
			}

			if (field?.name === "per") {
				field.initialValue = feePeriod;
				field.options = [feePeriod];
			}
		});
	};

	steps.forEach((step) => {
		updateFields(step.data);
		step.section?.forEach((section) => updateFields(section.data));
	});
};

const getFieldsFromRegistrationStep = (step) => {
	const fields = [];
	const collectFields = (items = []) => {
		items.forEach((item) => {
			if (Array.isArray(item)) {
				collectFields(item);
			} else if (item?.name) {
				fields.push(item);
			}
		});
	};

	collectFields(step?.data);
	step?.section?.forEach((section) => collectFields(section.data));

	return fields;
};

const isRequiredFieldInvalid = (field, values) => {
	if (!field?.required) return false;
	if (
		field.name === "howDidYouKnowAboutUsOthers" &&
		values.howDidYouHearAboutUs !== "Others"
	) {
		return false;
	}

	const value = values[field.name];

	if (field.type === "dropdown") {
		return value == null || emptySelectValues.has(value);
	}

	if (Array.isArray(value)) {
		return value.length === 0 || value.every((item) => `${item || ""}`.trim() === "");
	}

	if (field.type === "tel") {
		return !/^\+(?:[0-9] ?){6,14}[0-9]$/.test(`${value || ""}`);
	}

	if (field.type === "email") {
		return !/^[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/i.test(
			`${value || ""}`
		);
	}

	if (field.type === "number" && field.min != null && value !== "") {
		return Number(value) < Number(field.min);
	}

	return value == null || `${value}`.trim() === "";
};


const ChildRegister = ({
	childData,
	childEnrollment,
	registerData,
	handleToggle,
	isEnrollment,
	dataRegister,
	registrationScreen,
	...props
}) => {
	const { t } = useTranslation();

	const [activeTab, setActiveTab] = useState("0");
	const group = useSelector((state) => state.group);
	const section = useSelector((state) => state.section);
	const parentsData = useSelector((state) => state.parentsData);
	const nestedData = useSelector((state) => state.nestedData);
	const appConfig = useSelector((state) => state.config);
	const tokenState = useSelector((state) => state.token);
	const keywords = useSelector((state) => state.config.keywords);
	const [isChildProfile, setIsChildProfile] = useState(false);
	const [initialValues, setInitialValues] = useState({});
	const [formData, setFormData] = useState(null);
	const [childChangedValue, setChildChangedValue] = useState(null);
	const [motherChangedValue, setMotherChangedValue] = useState(null);
	const [fatherChangedValue, setFatherChangedValue] = useState(null);
	const [modalState, setModalState] = useState(false);
	const [isSuccess, setIsSuccess] = useState(true);
	const [modalType, setModalType] = useState("success");
	const [localModalMessage, setLocalModalMessage] = useState(null);
	const [comment, setComment] = useState(null);
	const [contentType, setContentType] = useState(true);
	const [country, setCountry] = useState("");
	const [addressStateOptions, setAddressStateOptions] = useState([""]);
	const [sessionList, setSessionList] = useState(null);
	const [isFastTrack, setIsFastTrack] = useState(false);
	const [childAddress, setChildAddress] = useState({
		line1: "",
		line2: "",
		city: "",
		country: "",
		postalCode: "",
		state: "",
	});
	  const nestedResponse = useFetch({ url: `/nested/section&group` });
	const daycareDetailsResponse = useFetch({ url: "/daycareDetails" });
	const configuredBillingType = getChildBillingTypeFromSources(
		daycareDetailsResponse.response,
		appConfig,
		tokenState?.token,
		getStoredConfig()
	);

	const [snackBarContent, setSnackBarContent] = useState(null);
	const snackBarRef = useRef();
	const [isErrorSnackBar, setIsErrorSnackBar] = useState(false);
	const dispatch = useDispatch();
	const skipNextDaycareBlurUpdateRef = useRef(false);
	const skipNextSessionBlurUpdateRef = useRef(false);
	// create child
	const accessibilityData = useSelector((state) => state).accessibility;
	const [siblingNamesArray, setSiblingNamesArray] = useState([]);
	const [siblingDOBArray, setSiblingDOBArray] = useState([]);

	const [deleteId, setDeleteId] = useState(null);
	const { response, error, isLoading } = useFetch({
		url: "/child/create",
		method: "POST",
		body: formData,
		contentType: false,
		accessibility: accessibilityData["Child Profile"],
	});
	let address = {};
	const isFastTrackAvailable = !childData && !childEnrollment;
	applyConfiguredBillingType(data, configuredBillingType);
	const registrationData = isFastTrackAvailable && isFastTrack
		? getFastTrackRegistrationData(data)
		: data.map((step, originalIndex) => ({ ...step, originalIndex }));
	const transportationAccess = accessibilityData?.Students;
	const canViewTransportation = childData && transportationAccess !== false && transportationAccess !== "No Access";
	const tabItems = childData
		? [...registrationData, { name: "Documents", originalIndex: 6, isDocuments: true }, ...(canViewTransportation ? [{ name: "Transportation", isTransportation: true }] : [])]
		: registrationData;

	const {
		values,
		handleChange,
		handleArrayChange,
		handleBlur,
		changedValue,
		errors,
		setErrors,
		checkErrors,
	} = useForms({
		initialValues: initialValues,
		isChildProfile: isChildProfile,
		defaultData: registrationData,
	});
	const childFullName =
		[values.firstName, values.lastName].filter(Boolean).join(" ") ||
		t("First name Last name");
	const childProfileMeta = childData
		? t("Registered child profile")
		: isFastTrack
			? t("Fast track registration")
			: t("Full registration");

	const updateAddressStateOptions = (countryName) => {
		const countryDetails = countries.find((ele) => ele.country === countryName);
		const nextOptions = countryDetails?.states?.length
			? countryDetails.states
			: [""];

		setAddressStateOptions(nextOptions);
	};

	const withAddressStateOptions = (fields = []) =>
		fields.map((field) =>
			field && !Array.isArray(field) && field.name === "addressState"
				? { ...field, options: addressStateOptions }
				: field
		);

	const createSyntheticSelectEvent = (id, value) => ({
		target: {
			id,
			name: id,
			type: "select-one",
			value,
			required: true,
			validity: {
				valueMissing: !value || value === "Select An Option",
				typeMismatch: false,
			},
		},
	});

	const updateDaycareDetails = (nextValues) => {
		if (!childData) return;
		const payload = {};
		if (nextValues.section && nextValues.section !== "Select An Option") {
			payload.sectionName = nextValues.section;
		}
		if (nextValues.group && nextValues.group !== "Select An Option") {
			payload.groupName = nextValues.group;
		}
		if (Object.keys(payload).length) {
			setContentType(true);
			setChildChangedValue(JSON.stringify(payload));
		}
	};

	const getSessionIdByName = (sessionName) =>
		sessionList?.find((session) => session.sessionName === sessionName)?._id;

	const updateSessionDetails = (sessionName) => {
		if (!childData || !sessionName || sessionName === "Select An Option") return;

		const sessionId = getSessionIdByName(sessionName);
		if (!sessionId) return;

		setContentType(true);
		setChildChangedValue(JSON.stringify({ sessionId }));
	};

	const handleRegisterChange = (event) => {
		if (event?.target?.id === "section") {
			const nextSection = event.target.value;
			const sectionGroups = nestedData[nextSection] || [];
			const nextGroup = sectionGroups[0] || "Select An Option";

			skipNextDaycareBlurUpdateRef.current = true;
			handleChange(event);
			handleChange(createSyntheticSelectEvent("group", nextGroup));
			updateDaycareDetails({ section: nextSection, group: nextGroup });
			return;
		}

		if (event?.target?.id === "group") {
			skipNextDaycareBlurUpdateRef.current = true;
			handleChange(event);
			updateDaycareDetails({
				section: values.section,
				group: event.target.value,
			});
			return;
		}

		if (event?.target?.id === "session") {
			skipNextSessionBlurUpdateRef.current = true;
			handleChange(event);
			updateSessionDetails(event.target.value);
			return;
		}

		if (event?.target?.id === "addressCountry") {
			updateAddressStateOptions(event.target.value);
			setCountry(event.target.value);
		}

		handleChange(event);
	};
	// useEffect(() => console.log("data enrollment", isEnrollment), [isEnrollment]);
	//delete prospect after registration
	const deleteProspectResponse = useFetch({
		url: `/child/prospect/deleteProspectChild`,
		method: "POST",
		body: deleteId,
		accessibility: accessibilityData["Child Profile"],
	});

	//this updates child details in request
	const childResponse = useFetch({
		url: `/updatechilddetails/${childData?._id}`,
		method: "PATCH",
		body: childChangedValue,
		accessibility: accessibilityData["Child Profile"],
		contentType: contentType,
	});
	const motherResponse = useFetch({
		url: `/updatemother/${childData?._id}`,
		method: "PATCH",
		body: motherChangedValue,
		contentType: contentType,
		accessibility: accessibilityData["Child Profile"],
	});
	const fatherResponse = useFetch({
		url: `/updatefather/${childData?._id}`,
		method: "PATCH",
		body: fatherChangedValue,
		contentType: contentType,
		accessibility: accessibilityData["Child Profile"],
	});
	const enrollChild = useFetch({
		url: `/enrollChild/edit/addComment/${childEnrollment?._id}`,
		method: "PATCH",
		body: comment,
		accessibility: accessibilityData["Child Profile"],
	});
	const getActiveSessions = useFetch({
		url: "/sessions/viewAll",
	});
	useEffect(() => {
		if (getActiveSessions.response) {
			const sessions = getActiveSessions.response.sessions;
			setSessionList(sessions);
			let sessionName = [];
			for (let i = 0; i < sessions.length; i++) {
				if (sessions[i].status === "Active") {
					sessionName.push(sessions[i].sessionName);
				}
			}
			//const sessionName = sessions.filter((ele) => ele.sessionName);
			data[4].data[10].options = sessionName;
			console.log(sessionName, data[4]);
		}
	}, [
		getActiveSessions.response,
		getActiveSessions.isLoading,
		getActiveSessions.error,
	]);

	
	


	  useEffect(() => {
		  console.log(
			"nested response",
			nestedResponse.response,
			nestedResponse.isLoading,
			nestedResponse.error
		  );
		  //nestedResponse.response?.message === null 
		  // adding above creates regression on the section group drop down
		  // if the new daycare manager id when there is no section data
		  //console.log("nestedResponse is:", nestedResponse.response);
		  if (nestedResponse.response && !nestedResponse.response.message) {
			const data = {};
			Object.entries(nestedResponse.response).map(([key, value]) => {
			  data[key] = Object.keys(value.groups);
			});
			//console.log("nested value", data);
			dispatch(nestedGroup(data));
			dispatch(addSectionObject(nestedResponse.response));
		  }
		}, [nestedResponse.response, nestedResponse.isLoading, nestedResponse.error]);
	
	
	




	//update address variables after childResponse;
	useEffect(() => {
		if (!childResponse.isLoading && childResponse.response) {
			address = childResponse.response.address;
			console.log({ address });
		}
	}, [childResponse.response, childResponse.isLoading]);

	useEffect(() => {
		//console.log("data is: ", data)
		//this is based on the index of group and section in the ./childProfileData file. if the data changes the index has to be changed.
		data[4].data[0].label = keywords.section;
		data[4].data[1].label = keywords.group;
		data[2].data[0].label = t("{{label}} Email Address", { label: t(keywords.child || "Child") });
		data[2].data[1].label = t("{{label}} Phone Number", { label: t(keywords.child || "Child") });
		details[4] = `${keywords.daycare} Details`;
		details[0] = `${keywords.child} Profile`;
		details[2] = `${keywords.child} Address`;
		//data[3].data[3].label = `Relationship With ${keywords.child}`;
		data[3].section[0].data[3].label = `Relationship With ${keywords.child}`;
		data[3].section[1].data[3].label = `Relationship With ${keywords.child}`;
		
		console.log("daycare details", data);
	}, [keywords.section]);

	const [changedStatus, setChangedStatus] = useState({});
	const statusResponse = useFetch({
		url: `/editEnrollmentButton/${changedStatus.id}`,
		body: changedStatus.body,
		method: "PATCH",
	});

	useEffect(() => {
		if (response) {
			setModalState(true);
			setIsSuccess(true);
			setModalType("success");
			setLocalModalMessage(null);
			if (registerData) {
				setDeleteId(JSON.stringify({ prospectChildID: registerData._id }));
			}
			if (dataRegister) {
				setChangedStatus({
					id: dataRegister._id,
					body: JSON.stringify({ status: "Confirmed" }),
				});
			}
		}
	}, [response]);

	useEffect(() => {
		if (error) {
			console.log("Error", error);
			setModalState(true);
			setIsSuccess(false);
			setModalType("error");
			setLocalModalMessage(null);
		}
	}, [error]);

	useEffect(() => {
		if (Number(activeTab) >= tabItems.length) {
			setActiveTab("0");
		}
	}, [activeTab, tabItems.length]);

	/*
	useEffect(() => {
		if (values.section != "Select an option") {
			let groups;
			if (nestedData[values.section]) {
				groups = [...nestedData[values.section]];
			}
			data[4].data[1].options = groups || [];
			console.log("datasections", nestedData[values.section]);
		}
	}, [values.section]);
	*/

	useEffect(() => {
		if (values.section && values.section !== "Select an option") {
			const groups = nestedData[values.section] || [];
			data[4].data[1].options = groups;

			// ✅ If only one group is available, auto-set it
			if (groups.length === 1 && values.group !== groups[0]) {
				const defaultGroup = groups[0];
				//const defaultGroup = "Select An Option";

				// Set in values
				handleChange({
					target: {
						id: "group", // the id used in form for group field
						type: "select-one", // mimic <select>
						value: defaultGroup,
					},
				});

				skipNextDaycareBlurUpdateRef.current = true;
				// Also trigger blur to capture changedValue
				handleBlur({
					target: {
						id: "group",
						type: "select-one",
						value: defaultGroup,
					},
				});


			}
		}
	}, [values.section]);

	useEffect(() => {
		if (
			values.section &&
			values.section !== "Select An Option" &&
			values.section !== "Select an option"
		) {
			const groups = nestedData[values.section] || [];
			data[4].data[1].options = groups;

			if (
				groups.length &&
				(!values.group ||
					values.group === "Select An Option" ||
					!groups.includes(values.group))
			) {
				handleChange(createSyntheticSelectEvent("group", groups[0]));
			} else if (!groups.length && values.group !== "Select An Option") {
				handleChange(createSyntheticSelectEvent("group", "Select An Option"));
			}
		}
	}, [values.section, nestedData]);

	useEffect(() => {
		console.log("mother photo", motherResponse.response);
		if (motherResponse.error) {
			console.log(motherResponse.error.toUpperCase());
			setSnackBarContent(motherResponse.error);
			setIsErrorSnackBar(true);
			snackBarRef.current.openSnackBar();
		}
		if (motherResponse?.error || motherResponse?.response) {
			setMotherChangedValue(null);
		}
	}, [motherResponse.response, motherResponse.error, motherResponse.isLoading]);

	useEffect(() => {
		console.log("mother photo", fatherResponse.response);
		if (fatherResponse.error) {
			console.log(fatherResponse.error);
			setSnackBarContent(fatherResponse.error);
			setIsErrorSnackBar(true);
			snackBarRef.current.openSnackBar();
		}
		if (fatherResponse?.error || fatherResponse?.response) {
			setFatherChangedValue(null);
		}
	}, [fatherResponse.response, fatherResponse.error, fatherResponse.isLoading]);

	const changePhotoHandler = (id, photo) => {
		console.log("parents id", id);
		const index = parentsData.findIndex((ele) => ele._id == id);
		const newParentsData = [...parentsData];
		newParentsData[index] = {
			...newParentsData[index],
			photo: URL.createObjectURL(photo),
		};
		console.log("new parents", newParentsData[index]);
		dispatch(setParents(newParentsData));
	};

	useEffect(() => {
		const sections = [];
		for (const [key, value] of Object.entries(nestedData)) sections.push(key);
		data[4].data[0].options = [...sections];
		console.log(
			"datasections",
			sections,
			data[4].data[0],
			section.map((ele) => ele.section)
		);
	}, [nestedData]);

	useEffect(() => {
		if (childData) {
			setChildAddress((prevState) => ({
				...prevState,
				line1: childData.addressLine1,
				line2: childData.addressLine2,
				city: childData.addressCity,
				country: childData.addressCountry,
				postalCode: childData.addressPostalCode,
				state: childData.addressState,
			}));
			console.log({ childData, childAddress });
			console.log("initial value", initialValues, values, childData);
			setInitialValues(childData);
			setSiblingNamesArray(childData.siblingName);
			setSiblingDOBArray(childData.siblingDOB);
			setIsChildProfile(true);
			setCountry(childAddress.country);
			setPrimaryContact(childData.primaryContact)
		}
	}, [initialValues]);

	const [primaryContact, setPrimaryContact] = useState("mother");

	// Update required fields based on primary contact - run synchronously before render
	// PRIMARY CONTACT CHANGE BELOW
	// const updatePrimaryContactFields = (contact) => {
	// 	const sections = data[1].section;
	// 	const motherData = sections[0].data;
	// 	const fatherData = sections[1].data;
	// 	const primaryData = contact === "mother" ? motherData : fatherData;
	// 	const secondaryData = contact === "mother" ? fatherData : motherData;
	// 	primaryData.forEach((field) => {
	// 		if (field.type === "file" || field.type === "email") return;
	// 		field.required = true;
	// 	});
	// 	secondaryData.forEach((field) => {
	// 		if (field.type === "file" || field.type === "email") return;
	// 		field.required = false;
	// 	});
	// 	const flatData = data[1].data;
	// 	flatData.forEach((field) => {
	// 		if (Array.isArray(field) || field.type === "file" || field.type === "email") return;
	// 		if (field.name.startsWith("mother")) {
	// 			field.required = contact === "mother";
	// 		} else if (field.name.startsWith("father")) {
	// 			field.required = contact === "father";
	// 		}
	// 	});
	// };
	// updatePrimaryContactFields(primaryContact);
	
	// update child information
	useEffect(() => {
		if (changedValue) {
			let primaryContact;
			let contenttype = true;
			let type = "child";
			console.log("changed values", changedValue);
			const name = Object.keys(changedValue)[0];
			if (name.includes("mother")) type = "mother";
			// if (name.includes("primaryContact")) {
			//   primaryContact = changedValue.primaryContact;
			//   const dt = data[1].section;
			//   let filteredData, requiredData;
			//   if (primaryContact == "mother") {
			//     filteredData = dt[2].data;
			//     requiredData = dt[1].data;
			//   } else if (primaryContact == "father") {
			//     filteredData = dt[1].data;
			//     requiredData = dt[2].data;
			//   }
			//   for (let i = 0; i < filteredData.length; i++) {
			//     if (filteredData[i].required) {
			//       filteredData[i].required = false;
			//     }
			//     if (requiredData[i].required) {
			//       requiredData[i].required = true;
			//     }
			//   }
			//   console.log(filteredData);
			// }
			if (name.includes("father")) type = "father";
			if (name == "comment") type = "comment";
			if (name.includes("address")) type = "address";
			if (name.includes("session")) type = "session";
			if (name.includes("group") || name.includes("section"))
				type = "groupsection";
			/*if (name.includes("group") )
				type = "group";
			if (name.includes("section"))
				type = "section";*/
			if (name.includes("sibling")) type = "sibling";
			if (name.includes("Hear") || name.includes("KnowAbout"))
				type = "heardAbout";
			//checks if the photo has been changed
			if (name.toLowerCase().includes("photo")) {
				const formData = new FormData();
				if (type == "child") {
					console.log("changed values data", changedValue);
					formData.append("photo", changedValue.photo);
					setChildChangedValue(formData);
				} else if (type == "mother") {
					console.log("mothers photo");
					formData.append("photo", changedValue.motherPhoto);
					if (childData) {
						changePhotoHandler(childData.motherId, changedValue.motherPhoto);
						setMotherChangedValue(formData);
					}
				} else {
					console.log("father photo");
					formData.append("photo", changedValue.fatherPhoto);
					if (childData) setFatherChangedValue(formData);
				}
				contenttype = false;
				type = "null";
			}

			setContentType(contenttype);
			switch (type) {
				case "child": {
					setChildChangedValue(JSON.stringify(changedValue));
					break;
				}
				case "sibling": {
					console.log(changedValue);
					console.log(Object.keys(changedValue)[0]);
					const key = Object.keys(changedValue)[0];
					const arrayValues = Object.values(changedValue)[0];
					console.log({ arrayValues });
					let updateObject = [];
					if (key === "siblingDOB") {
						let siblingNames = siblingNamesArray;
						console.log({ siblingNamesArray, siblingDOBArray, siblingNames });
						setSiblingDOBArray(arrayValues);
						setSiblingNamesArray(siblingNames);
						for (let i = 0; i < arrayValues.length; i++) {
							let obj = {
								siblingName: siblingNames[i],
								siblingDOB: arrayValues[i],
							};
							updateObject.push(obj);
						}
					} else if (key == "siblingName") {
						let siblingDOB = siblingDOBArray;
						console.log({ siblingNamesArray, siblingDOBArray, siblingDOB });
						setSiblingNamesArray(arrayValues);
						setSiblingDOBArray(siblingDOB);
						for (let i = 0; i < arrayValues.length; i++) {
							let obj = {
								siblingName: arrayValues[i],
								siblingDOB: siblingDOB[i],
							};
							updateObject.push(obj);
						}
					}
					console.log({ updateObject });
					setChildChangedValue(JSON.stringify({ siblings: updateObject }));
					break;
				}
				case "heardAbout": {
					const values = Object.values(changedValue)[0];
					const key = Object.keys(changedValue)[0];
					if (key == "howDidYouKnowAboutUsOthers") {
						setChildChangedValue(
							JSON.stringify({ howDidYouHearAboutUS: "Others" + "-" + values })
						);
					} else {
						setChildChangedValue(
							JSON.stringify({ howDidYouHearAboutUS: values })
						);
					}
					break;
				}
				case "mother": {
					const fieldChange = {
						motherName: "firstName",
						motherLastName: "lastName",
						motherEmail: "email",
						motherMobile: "mobile",
					};
					setMotherChangedValue(
						JSON.stringify({ [fieldChange[name]]: changedValue[name] })
					);
					break;
				}
				case "father": {
					const fieldChange = {
						fatherName: "firstName",
						fatherLastName: "lastName",
						fatherEmail: "email",
						fatherMobile: "mobile",
					};
					setFatherChangedValue(
						JSON.stringify({ [fieldChange[name]]: changedValue[name] })
					);
					break;
				}
				case "comment": {
					setComment(JSON.stringify({ comment: changedValue.comment }));
					break;
				}
				case "address": {
					if (name === "childEmail" || name === "childMobile") {
						setChildChangedValue(JSON.stringify({ [name]: changedValue[name] }));
						break;
					}
					//console.log("Address change detected...");
					let updatedAddress = { ...childAddress };

					if (name.includes("Country")) {
						setCountry(changedValue.addressCountry);
						updatedAddress.country = changedValue.addressCountry;

						const value = changedValue.addressCountry;
						const dt = countries.find((ele) => ele.country === value);
						const add = data.find((ele) => ele.name === "address");

						if (dt && add) {
							const val = dt.states;
							const values = add.data.find((ele) => ele.name === "addressState");
							if (values) {
								values.options = val;
							}
						}
					}
					if (name.includes("Line1")) {
						updatedAddress.line1 = changedValue.addressLine1;
					} else if (name.includes("Line2")) {
						updatedAddress.line2 = changedValue.addressLine2;
					} else if (name.includes("City")) {
						updatedAddress.city = changedValue.addressCity;
					} else if (name.includes("Code")) {
						updatedAddress.postalCode = changedValue.addressPostalCode;
					} else if (name.includes("State")) {
						updatedAddress.state = changedValue.addressState;
					}

					console.log("Updated Address: ", updatedAddress);

					setChildAddress(updatedAddress);
					setChildChangedValue(JSON.stringify({ address: updatedAddress }));
					break;
				}

				case "session": {
					if (skipNextSessionBlurUpdateRef.current) {
						skipNextSessionBlurUpdateRef.current = false;
						break;
					}
					//console.log(sessionList);
					const newValue = Object.values(changedValue)[0];
					console.log(newValue);
					const val = sessionList?.find((ele) => ele.sessionName === newValue);
					console.log(val);
					if (val?._id) {
						setChildChangedValue(JSON.stringify({ sessionId: val._id }));
					}
					break;
				}
				/*	case "groupsection": {
						const val = Object.values(changedValue)[0];
						console.log("changedValue is: ", changedValue);
						console.log(val);
						if (name.includes("group") && val !== "Select An Option") {
							setChildChangedValue(JSON.stringify({ groupName: val }));
						} else if (name.includes("section")) {
							setChildChangedValue(JSON.stringify({ sectionName: val }));
						}
						break;
					}*/

				case "groupsection": {
					if (skipNextDaycareBlurUpdateRef.current) {
						skipNextDaycareBlurUpdateRef.current = false;
						break;
					}
					const val = Object.values(changedValue)[0];
					console.log("changedValue is: ", changedValue);
					console.log(val);

					if (name.includes("group") && val !== "Select An Option") {
						setChildChangedValue(JSON.stringify({ groupName: val }));
					} else if (name.includes("section")) {
						const payload = { sectionName: val };

						// 🔁 Send another update for the corresponding group
						const groups = nestedData[val] || [];

						if (groups.length > 0) {
							const firstGroup = groups[0];
							if (firstGroup && firstGroup !== "Select An Option") {
								payload.groupName = firstGroup;
							}
						}
						setChildChangedValue(JSON.stringify(payload));
					}
					break;
				}

				/*	case "section": {
						const val = Object.values(changedValue)[0];
						console.log("IN SECTION - changedValue is: ", changedValue);
						console.log(val);
						if (name.includes("section")) {
							setChildChangedValue(JSON.stringify({ sectionName: val }));
						}
						break;
					}
	
					case "group": {
						const val = Object.values(changedValue)[0];
						console.log("IN GROUP - changedValue is: ", changedValue);
						console.log(val);
						if (name.includes("group")) {
							setChildChangedValue(JSON.stringify({ groupName: val }));
						}
						break;
					}*/
			}
		}
	}, [changedValue]);

	useEffect(() => {
		if (childEnrollment) {
			console.log("initial value", initialValues, childEnrollment);
			setIsChildProfile(isEnrollment);
			setInitialValues(childEnrollment);
		}
	}, [initialValues]);

	//fills up the data if the student is enrolled
	useEffect(() => {
		//console.log("Inside enrollment filling: ", data)
		if (group && section) {
			let values = {};
			let sectionData = {};
			const newData = [...data];
			console.log(newData);
			//map through all the data in data.js
			newData.forEach((ele) => {
				values = {
					...values,
					...(ele.section
						? ele.section.forEach((element) => {
							const listOfValues = element.data;
							let emptyObj = {};
							const valuesTakeOut = listOfValues.map(
								(el) =>
									Array.isArray(el) === false && {
										...emptyObj,
										[el.name]: el.initialValue,
									}
							);
							let finalObj = {};
							for (let i = 0; i < valuesTakeOut.length; i++) {
								finalObj = { ...finalObj, ...valuesTakeOut[i] };
							}
							const returnValue = { ...finalObj };
							//console.log("some values",{ returnValue, ...returnValue, ...[returnValue] });
							sectionData = { ...sectionData, ...returnValue };
						})
						: ele.data.reduce(
							(obj, current) =>
								Array.isArray(current)
									? {
										...obj,
										...current.reduce(
											(obj, current) => ({
												...obj,
												[current.name]: current.initialValue,
											}),
											{}
										),
									}
									: { ...obj, [current.name]: current.initialValue },
							{}
						)),
				};
			});
			values = { ...values, ...sectionData };
			if (registerData) {
				values = { ...values, ...registerData };
				setCountry(registerData?.addressCountry);
			}
			console.log({ newData, registerData, sectionData });
			console.log(`values`, { values, registerData });
			if (!dataRegister) {
				values.siblingName = [""];
				values.siblingDOB = [""];
				setInitialValues(() => values);
			} else {
				setInitialValues(() => ({ ...values, ...dataRegister }));
			}
		}
	}, [data, section, group, configuredBillingType]);

	useEffect(() => {
		if (childData || childEnrollment) return;

		const normalizedBillingType = normalizeChildBillingType(configuredBillingType);
		const feePeriod = normalizedBillingType === "Monthly" ? "Month" : "Term";

		if (values.billing !== normalizedBillingType) {
			handleChange(createSyntheticSelectEvent("billing", normalizedBillingType));
		}
		if (values.per !== feePeriod) {
			handleChange(createSyntheticSelectEvent("per", feePeriod));
		}
	}, [configuredBillingType]);

	//change state dropdown when country is changed
	useEffect(() => {
		updateAddressStateOptions(values.addressCountry || country);
	}, [country, values.addressCountry]);

	const validateRegistrationBeforeSubmit = () => {
		const nextErrors = {};
		let firstInvalidTab = null;

		registrationData.forEach((step, stepIndex) => {
			const fields = getFieldsFromRegistrationStep(step);

			fields.forEach((field) => {
				if (isRequiredFieldInvalid(field, values)) {
					nextErrors[field.name] = true;
					if (firstInvalidTab === null) {
						firstInvalidTab = stepIndex;
					}
				}
			});
		});

		setErrors(nextErrors);

		if (firstInvalidTab !== null) {
			setActiveTab(`${firstInvalidTab}`);
			setIsSuccess(false);
			setModalType("warning");
			setLocalModalMessage(
				`Please fill all mandatory fields before submitting the ${keywords.child?.toLowerCase() || "child"} registration form.`
			);
			setModalState(true);
			return true;
		}

		return false;
	};

	const submitHandler = (index) => {
		//const error = checkErrors(index);
		let error = false;
		console.log(error);
		console.log("Submit handler called!", values);
		if (validateRegistrationBeforeSubmit()) {
			return;
		}
		const formdata = new FormData();
		values.primaryContact = primaryContact;
		Object.keys(values).forEach((key) => {
			if (key == "photo") {
				if (values[key]) formdata.append("photos", values[key]);
			} else if (key == "motherPhoto") {
				if (values[key]) formdata.append("photos", values[key]);
			} else if (key == "fatherPhoto") {
				if (values[key]) formdata.append("photos", values[key]);
			} else if (key == "addressLine1") {
				formdata.append("line1", values[key]);
			} else if (key == "addressLine2") {
				formdata.append("line2", values[key]);
			} else if (key == "addressCity") {
				formdata.append("city", values[key]);
			} else if (key == "addressCountry") {
				formdata.append("country", values[key]);
			} else if (key == "addressPostalCode") {
				formdata.append("postalCode", values[key]);
			} else if (key == "addressState") {
				formdata.append("state", values[key]);
			} else if (key == "session") {
				const newValue = values[key];
				console.log(newValue);
				const val = sessionList.find((ele) => ele.sessionName === newValue);
				console.log({ val });
				if (val === undefined && newValue !== undefined) {
					formdata.append("sessionId", newValue?._id);
				} else if (val !== undefined) {
					formdata.append("sessionId", val?._id);
				}
			} /* probably we dont need this as its adding twice the sessionId and hence the issue "Line 118, child route,  Error: Children validation failed: associatedSession: Cast to ObjectId failed for value "[ '680aa9700d984421ec2794c3', '680aa9700d984421ec2794c3' ]" (type Array) at path "associatedSession""
				else if (key == "associatedSession") {
				console.log("ASSOCIATEDSESSION IS.....", key)
				if (values[key]?._id) {
					console.log("ASSOCIATEDSESSION - key value IS.....", values[key]?._id)
					formdata.append("sessionId", values[key]?._id);
				} else {
					return;
				}
			}*/ else if (key == "group") {
				formdata.append("groupName", values[key]);
			} else if (key == "section") {
				formdata.append("sectionName", values[key]);
			}
			// RM Comment - making vaccine info non-mandatory for Sparkle implmentation
			/*else if (key == "vaccinationType") {
				console.log(values[key][0].length === 0);
				if (values[key][0].length === 0) {
					error = true;
					return;
				} else {
					error = false;
					console.log("new key", key);
					formdata.append(key, values[key]);
				}
			} else if (key == "vaccinationLastDate") {
				console.log(values[key]);
				if (values[key][0].length === 0) {
					error = true;
					return;
				} else {
					error = false;
					console.log("new key", key);
					formdata.append(key, values[key]);
				}
			}*/ else if (key === "primaryContact") {
				// PRIMARY CONTACT CHANGE BELOW
				// formdata.append("primaryContact", primaryContact);
			} else if (key === "exteriorTripAuthorization") {
				console.log("new key", key);
				const val = values[key] === "Select An Option" ? "" : values[key];
				formdata.append(key, val);
				formdata.append("consent", true);
			} else if (key === "photoVideoAuthorization" || key === "bloodGroup") {
				const val = values[key] === "Select An Option" ? "" : values[key];
				formdata.append(key, val);
			} else if (key === "howDidYouHearAboutUs") {
				if (values[key] === "Select An Option") {
					formdata.append("howDidYouHearAboutUS", "");
				} else if (values[key] === "Others") {
					const othersInfo = values["howDidYouKnowAboutUsOthers"];
					formdata.append(
						"howDidYouHearAboutUS",
						values[key] + "-" + othersInfo
					);
				} else {
					formdata.append("howDidYouHearAboutUS", values[key]);
				}
			} else if (key == "siblingName") {
				let siblings = [];
				if (values.siblingName && values.siblingDOB) {
					const siblingNamesArray = values["siblingName"];
					const siblingDOBArray = values["siblingDOB"];
					for (let i = 0; i < siblingNamesArray.length; i++) {
						siblings.push({
							siblingName: siblingNamesArray[i] || "",
							siblingDOB: siblingDOBArray[i] || "",
						});
					}
				}
				formdata.append("siblings", JSON.stringify(siblings));
			} 
			else if (key == "registeredSibling") {
				formdata.append("registeredSibling", values[key]);
			} 
			else if (
				key != "associatedSession" ||
				key != "group" ||
				key != "section" ||
				key != "session"
			) {
				console.log("new key", key);
				if (key != "siblings") {
					formdata.append(key, values[key]);
				}
			}
		});
		if (formdata.get("group")) {
			formdata.delete("group");
		}
		if (formdata.get("section")) {
			formdata.delete("section");
		}
		if (formdata.get("associatedSession")) {
			formdata.delete("associatedSession");
		}
		if (formdata.get("siblingsDOB")) {
			formdata.delete("siblingDOB");
		}
		//Keeping this console logs here for faster debugging in future
		//
		// This logs all the all the values filled in the childRegistration form as => key, value
		// Object.keys(values).forEach((key) => {
		//   console.log(key, values[key]);
		// });
		//
		// This loop logs all the formData as => key, value
		for (var pair of formdata.entries()) {
			console.log(pair[0] + ", " + pair[1]);
		}
		if (error === false) {
			console.log(formdata);
			setFormData(formdata);
			console.log("Going to rediect to child listing");
		} else {
			console.log("Error");
		}
	};
	const nextTabHandler = (index) => {
		if (index < registrationData.length - 1 && !checkErrors(index)) {
			setActiveTab(`${index + 1}`);
		}
	};

	const previousTabHandler = (index) => {
		setActiveTab(`${index - 1}`);
	};

	const closeResultModal = () => {
		setModalState(false);
		modalType === "success" && handleToggle && response && !error
			? handleToggle()
			: null;
	};

	const modalMessage = localModalMessage
		? localModalMessage
		: isSuccess
		? `${keywords.child} registered successfully.`
		: typeof error === "string"
			? error?.charAt(0).toUpperCase() + error?.slice(1)
			: "Critical error has occurred";
	const modalTitle =
		modalType === "warning" ? "Missing required details" : isSuccess ? "Success" : "Error";
	const modalIcon =
		modalType === "warning" ? "warning" : isSuccess ? "check_circle" : "error";

	return (
		<div
			className={styles.registerShell}
			style={{
				paddingTop: `${childData || childEnrollment || registrationScreen ? 0 : 70}px`,
			}}
		>
			<MDBModal
				size="md"
				centered
				isOpen={modalState}
				toggle={closeResultModal}
				disableBackdrop
				className={styles.resultModal}
			>
				<MDBModalBody className={styles.resultModalBody}>
					<button
						type="button"
						className={styles.modalCloseButton}
						onClick={closeResultModal}
						aria-label={t("Close")}
					>
						<MDBIcon icon="times" />
					</button>
					<div
						className={`${styles.resultIcon} ${
							modalType === "warning"
								? styles.resultIconWarning
								: isSuccess
									? styles.resultIconSuccess
									: styles.resultIconError
						}`}
					>
						<span className="material-icons">{modalIcon}</span>
					</div>
					<h2 className={styles.resultTitle}>{t(modalTitle)}</h2>
					<p className={styles.resultMessage}>{modalMessage}</p>
				</MDBModalBody>
				<MDBModalFooter className={styles.resultFooter}>
					<Button
						className={styles.resultButton}
						onClick={() => {
							closeResultModal();
						}}
					>
						{t("Ok")}
					</Button>
				</MDBModalFooter>
			</MDBModal>
			{isLoading ? (
				<Loader />
			) : (
				<>
					<SnackBar
						content={snackBarContent}
						isError={isErrorSnackBar}
						ref={snackBarRef}
					/>
					<div className={styles.formHeader}>
						<div>
							<p className={styles.eyebrow}>{`${t(keywords.child)} Management`}</p>
							<div className={styles.titleRow}>
								<h1 className={styles.title}>
									{childData ? t("Child Profile") : registerChildLabel(t, keywords)}
								</h1>
								{!childData ? (
									<TutorialLink href="https://stepswatchstorage-production.s3.ap-south-1.amazonaws.com/tutorial/child-registration.mp4" />
								) : null}
							</div>
						</div>
						{isFastTrackAvailable ? (
							<div className={styles.registrationMode} role="radiogroup" aria-label={t("Registration mode")}>
								<span className={styles.modeLabel}>{t("Mode")}</span>
								<div className={styles.modeOptions}>
									<button
										type="button"
										className={`${styles.modeOption} ${!isFastTrack ? styles.modeOptionActive : ""}`}
										onClick={() => {
											setIsFastTrack(false);
											setActiveTab("0");
										}}
										role="radio"
										aria-checked={!isFastTrack}
									>
										<span className="material-icons">assignment</span>
										<span>{t("Full")}</span>
									</button>
									<button
										type="button"
										className={`${styles.modeOption} ${isFastTrack ? styles.modeOptionActive : ""}`}
										onClick={() => {
											setIsFastTrack(true);
											setActiveTab("0");
										}}
										role="radio"
										aria-checked={isFastTrack}
									>
										<span className="material-icons">bolt</span>
										<span>{t("Fast Track")}</span>
									</button>
								</div>
							</div>
						) : null}
					</div>
					<div className={styles.childIdentityHeader}>
						<div className={styles.childIdentity}>
							<div className={styles.childIdentityPhoto}>
								<InputImage
									name="photo"
									label="Add Avatar"
									value={values.photo}
									initials={getInitials(values.firstName, values.lastName)}
									onChange={handleRegisterChange}
									disabled={
										isEnrollment ||
										accessibilityData["Child Profile"] === "Read"
									}
									width={128}
									height={128}
								/>
							</div>
							<div className={styles.childIdentityText}>
								<p className={styles.childIdentityEyebrow}>
									{childData
										? t(`${keywords.child} profile`)
										: t(`Create a ${keywords.child}`)}
								</p>
								<h2>{childFullName}</h2>
								<p>{childProfileMeta}</p>
							</div>
						</div>
					</div>
					<div className={styles.tabs} role="tablist">
						{tabItems.map((step, index) => {
							const label = step.isTransportation ? "Transportation" : details[step.originalIndex ?? index];

							return (
							<button
								key={`${label}-${index}`}
								type="button"
								style={{
									display: `${step.isDocuments && !childData ? "none" : ""}`,
								}}
								className={[
									styles.button,
									activeTab === `${index}` ? styles.active : "",
								].join(" ")}
								onClick={() => setActiveTab(`${index}`)}
								role="tab"
								aria-selected={activeTab === `${index}`}
							>
								<span className={styles.stepNumber}>{index + 1}</span>
								<span className={styles.stepLabel}>{t(`${label}`)}</span>
							</button>
						);
						})}
					</div>
					<MDBTabContent
						activeItem={activeTab}
						className={styles.tabContent}
					// style={{ overflow: "auto" }}
					>
						{registrationData.map((ele, index) => (
							<MDBTabPane
								key={ele.name || index}
								tabId={`${index}`}
								className={styles.tabPane}
							// style={{ overflow: "hidden" }}
							>
								<form
									onSubmit={(event) => event.preventDefault()}
									className={styles.form}
								>
									{ele.section
										? ele.section.map((element, sectionIndex) => (
											<div key={element.name || sectionIndex} className={styles.sectionBlock}>
												{element.name && !element.sectionHeading && (
													<div className={styles.sectionHeader}>
														<h2>{t(element.name)}</h2>
													</div>
												)}
												{element.sectionHeading && (
													<div className={styles.sectionHeader}>
														<h2>
															{t(element.sectionHeading)}
														</h2>
													</div>
												)}
												{/*PRIMARY CONTACT CHANGE BELOW */}
												{/* {element.primaryContactOption && (
													<div className="d-flex align-items-center justify-content-center pt-3 mb-0">
														<h6 className="mb-0 me-2 mr-4">{t(element.name)}</h6>
														<label className="d-flex align-items-center mb-0" style={{ cursor: "pointer", gap: "4px" }}>
															<input
																type="checkbox"
																checked={
																	(element.name === "Mother's Details" && primaryContact === "mother") ||
																	(element.name === "Father's Details" && primaryContact === "father")
																}
																onChange={() => {
																	const contact = element.name === "Mother's Details" ? "mother" : "father";
																	updatePrimaryContactFields(contact);
																	setPrimaryContact(contact);
																	if (childData) {
																		setChildChangedValue(JSON.stringify({ primaryContact: contact }));
																	}
																}}
															/>
															<span style={{ fontSize: "14px" }}>{t("Primary Contact")}</span>
														</label>
													</div>
												)} */}

												<div
													className={styles.card}
												>
													<InputRenderer
														disabled={
															accessibilityData["Child Profile"] === "Read"
														}
														modernDates={true}
														handleChange={handleRegisterChange}
														handleArrayChange={handleArrayChange}
														handleBlur={handleBlur}
														values={values}
														data={withoutChildPhotoField(
															withAddressStateOptions(element.data)
														)}
														errors={errors}
														isChildProfile={childData ? true : false}
														isEnrollment={isEnrollment}
													/>
												</div>
											</div>
										))
										: null}
									{!ele.section ? (
										<div className={styles.card}>
											<InputRenderer
												disabled={accessibilityData["Child Profile"] === "Read"}
												modernDates={true}
												handleChange={handleRegisterChange}
												handleArrayChange={handleArrayChange}
												handleBlur={handleBlur}
												values={values}
												data={withoutChildPhotoField(
													withAddressStateOptions(ele.data)
												)}
												errors={errors}
												isChildProfile={childData ? true : false}
												isEnrollment={childEnrollment ? true : false}
											/>{" "}
										</div>
									) : null}
									{childData || childEnrollment ? null : (
										<div className={styles.formActions}>
											<div className={styles.actionButtons}>
												{index == 0 ? null : (
													<MDBBtn
														onClick={() => previousTabHandler(index)}
														className={styles.secondaryAction}
														type="button"
													>
														<MDBIcon icon="chevron-left" />
														{t("back")}
													</MDBBtn>
												)}
												<MDBBtn
													onClick={() =>
														registrationData.length - 1 === index
															? submitHandler(index)
															: nextTabHandler(index)
													}
													className={styles.primaryAction}
													type="submit"
													disabled={
														registrationData.length - 1 === index &&
														accessibilityData["Child Profile"] === "Read"
													}
												>
													{`${registrationData.length - 1 === index
															? `${t("submit")}`
															: `${t("next")}`
														}`}
													{registrationData.length - 1 === index ? null : (
														<MDBIcon icon="chevron-right" />
													)}
												</MDBBtn>
											</div>
										</div>
									)}
								</form>
							</MDBTabPane>
						))}
						<MDBTabPane tabId={`${registrationData.length}`} className={styles.tabPane}>
							<DocumentsTab data={childData} />
						</MDBTabPane>
						{canViewTransportation ? (
							<MDBTabPane tabId={`${registrationData.length + 1}`} className={styles.tabPane}>
								<ChildTransportation childId={childData?._id || childData?.id} />
							</MDBTabPane>
						) : null}
					</MDBTabContent>
				</>
			)}
		</div>
	);
};

export default ChildRegister;
